import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ConfirmModal } from './ConfirmModal';
import { ClipboardList, AlertCircle } from 'lucide-react';

const SOURCE_TYPES = [
  { value: 'retail_food_order', label: 'Retail/Food Order', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'b2b_sale_order', label: 'B2B Sale Order', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  { value: 'room_booking', label: 'Room Booking', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  { value: 'table_reservation', label: 'Table Reservation', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  { value: 'ride', label: 'Ride', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
  { value: 'delivery', label: 'Delivery', color: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400' },
];

function SourceBadge({ type }: { type: string }) {
  const found = SOURCE_TYPES.find((s) => s.value === type);
  return (
    <span className={`px-2 py-1 rounded text-xs font-semibold ${found?.color || 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'}`}>
      {found?.label || type}
    </span>
  );
}

const PAGE_SIZE = 50;

export function OrderBookingControlView() {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState<{ id: string; business_name: string }[]>([]);

  const [tenantFilter, setTenantFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);

  const [disputeTarget, setDisputeTarget] = useState<any>(null);
  const [disputeSubject, setDisputeSubject] = useState('');
  const [disputeMessage, setDisputeMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastTicketId, setLastTicketId] = useState<string | null>(null);

  useEffect(() => {
    const fetchTenants = async () => {
      const supabase = getSupabaseClient();
      if (!supabase) return;
      const { data } = await supabase.from('tenants').select('id, business_name').order('business_name');
      if (data) setTenants(data);
    };
    fetchTenants();
  }, []);

  const fetchOrders = async () => {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setLoading(true);
    const { data, error } = await supabase.rpc('admin_list_unified_orders', {
      p_tenant_id: tenantFilter === 'all' ? null : tenantFilter,
      p_source_type: typeFilter === 'all' ? null : typeFilter,
      p_status: statusFilter === 'all' ? null : statusFilter,
      p_limit: PAGE_SIZE,
      p_offset: page * PAGE_SIZE,
    });
    if (error) {
      toast.error(error.message || 'Failed to load orders');
    } else {
      setOrders(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantFilter, typeFilter, statusFilter, page]);

  const handleRaiseDispute = async () => {
    if (!disputeTarget || !disputeSubject.trim() || !disputeMessage.trim()) {
      toast.error('Subject aur message dono zaroori hain');
      return;
    }
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setSubmitting(true);
    const { data, error } = await supabase.rpc('admin_open_order_dispute', {
      p_source_type: disputeTarget.source_type,
      p_order_id: disputeTarget.id,
      p_subject: disputeSubject.trim(),
      p_message: disputeMessage.trim(),
    });
    if (error) {
      toast.error(error.message || 'Dispute create nahi ho paaya');
    } else {
      setLastTicketId(data);
      toast.success(`Ticket ban gaya, Page 3 ke Support Tickets mein dekho`);
      setDisputeTarget(null);
      setDisputeSubject('');
      setDisputeMessage('');
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div>
        <h1 className="text-2xl font-bold dark:text-white">Order & Booking Control</h1>
        <p className="text-slate-500 dark:text-slate-400">Unified cross-vertical view of every order, booking and ride on the platform.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Select value={tenantFilter} onValueChange={(v) => { setTenantFilter(v); setPage(0); }}>
          <SelectTrigger className="w-56 dark:bg-slate-800 dark:border-slate-700 dark:text-white">
            <SelectValue placeholder="All Tenants" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tenants</SelectItem>
            {tenants.map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.business_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(0); }}>
          <SelectTrigger className="w-56 dark:bg-slate-800 dark:border-slate-700 dark:text-white">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {SOURCE_TYPES.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
          <SelectTrigger className="w-44 dark:bg-slate-800 dark:border-slate-700 dark:text-white">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="dark:bg-slate-900 dark:border-slate-800 border-none shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 font-medium">
              <tr>
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3">Business</th>
                <th className="px-6 py-3">Customer</th>
                <th className="px-6 py-3">Amount</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {loading && (
                <tr><td colSpan={7} className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">Loading orders...</td></tr>
              )}
              {!loading && orders.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                    <ClipboardList className="mx-auto mb-2 text-slate-300 dark:text-slate-700" size={28} />
                    No orders found for this filter.
                  </td>
                </tr>
              )}
              {!loading && orders.map((o) => (
                <tr key={`${o.source_type}-${o.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="px-6 py-3"><SourceBadge type={o.source_type} /></td>
                  <td className="px-6 py-3 font-semibold dark:text-white">{o.business_name}</td>
                  <td className="px-6 py-3 dark:text-slate-300">
                    {o.customer_name || '—'}
                    {o.customer_phone && <span className="block text-xs text-slate-400">{o.customer_phone}</span>}
                  </td>
                  <td className="px-6 py-3 dark:text-slate-300">₹ {Number(o.amount || 0).toLocaleString('en-IN')}</td>
                  <td className="px-6 py-3">
                    <span className="px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs font-medium capitalize">
                      {o.status}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-slate-500 dark:text-slate-400 text-xs">{new Date(o.created_at).toLocaleString()}</td>
                  <td className="px-6 py-3 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="dark:border-slate-700 dark:text-white gap-1"
                      onClick={() => setDisputeTarget(o)}
                    >
                      <AlertCircle size={14} /> Raise Dispute
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between items-center p-3 border-t dark:border-slate-800">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="dark:border-slate-700 dark:text-white">
            Previous
          </Button>
          <span className="text-xs text-slate-400">Page {page + 1}</span>
          <Button variant="outline" size="sm" disabled={orders.length < PAGE_SIZE} onClick={() => setPage((p) => p + 1)} className="dark:border-slate-700 dark:text-white">
            Next
          </Button>
        </div>
      </Card>

      {lastTicketId && (
        <p className="text-xs text-emerald-600 dark:text-emerald-400">
          Ticket #{lastTicketId.substring(0, 8)}... ban gaya — Support & Tickets tab mein dekhein.
        </p>
      )}

      <ConfirmModal
        open={!!disputeTarget}
        title="Raise Order Dispute"
        confirmLabel={submitting ? 'Submitting...' : 'Create Ticket'}
        loading={submitting}
        onConfirm={handleRaiseDispute}
        onCancel={() => { setDisputeTarget(null); setDisputeSubject(''); setDisputeMessage(''); }}
      >
        <div className="space-y-3">
          <Input
            placeholder="Subject"
            value={disputeSubject}
            onChange={(e) => setDisputeSubject(e.target.value)}
            className="dark:bg-slate-800 dark:border-slate-700 dark:text-white"
          />
          <textarea
            placeholder="Message"
            value={disputeMessage}
            onChange={(e) => setDisputeMessage(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
          />
        </div>
      </ConfirmModal>
    </div>
  );
}

export default OrderBookingControlView;
